import React from 'react'
import PropTypes from 'prop-types'
import { Link } from '../../../core'

/* doc
---
title: Button
name: Button
category: React - Bulma
---

A `Button` component based on Bulma `.button`. This can be used to add either
a `<button>` or `<a>` element that function as buttons.

This renders the basic button styles, if you need a different style of button,
you should use one of the other variants listed below.

See [Bulma Documentation](http://bulma.io/documentation/elements/button/) for reference and full options.
If one of the Bulma options isn't listed below, it may be added using the `className` prop.

### Props
| Prop Name       | Description                                   |Type            | Default Value             | Required?     |
|-----------------|-----------------------------------------------|----------------|---------------------------|---------------|
| disabled        | Disables the button                           | boolean        | false                     | no            |
| href            | Renders a link instead of a button            | string         | ''                        | no            |
| isActive        | Adds active state styles to button            | boolean        | false                     | no            |
| isFullwidth     | Makes button fullwidth to container           | boolean        | false                     | no            |
| isInverted      | Adds inverted button style                    | boolean        | false                     | no            |
| isLoading       | Adds loading button style and disables button | boolean        | false                     | no            |
| isOutlined      | Adds outlined button style                    | boolean        | false                     | no            |
| isText          | Adds clear color to button for text only      | boolean        | false                     | no            |
| size            | Button size (small, medium, large)            | string         | ''                        | no            |
| trackEvent      | If click event should be tracked.             | boolean        | false                     | no            |
| eventCategory   | GA event category.                            | string         |                           | no            |
| eventAction     | GA event action.                              | string         |                           | no            |
| eventLabel      | GA event label.                               | string         |                           | no            |
| eventValue      | GA event value.                               | number         |                           | no            |

```html_example
  <Button
    size="medium"
    isOutlined
    isLoading={formSubmitting}
  >
    ...
  </Button>
```
*/

/**
* @TODO Add support for icons.
*/
function Button(props) {
  const {
    children, className, disabled, href, isActive, isFullwidth, isInverted, isLoading, isOutlined, isText, size, trackEvent, eventCategory, eventAction, eventLabel, eventValue, onClick, ...rest
  } = props

  const classes = [
    'button',
    className || '',
    size ? `is-${size}` : '',
    isOutlined ? 'is-outlined' : '',
    isText ? 'is-text' : '',
    isInverted ? 'is-inverted' : '',
    isFullwidth ? 'is-fullwidth' : '',
    isLoading ? 'is-loading' : '',
    isActive ? 'is-active' : '',
  ]

  /**
  * Wrapper for onClick to track click event in GA if requested.
  */
  const onClickHandler = (e) => {
    if (trackEvent) {
      e.preventDefault()
      $.track('event', eventCategory, eventAction, eventLabel, eventValue, true, () => {
        if (typeof onClick === 'function') {
          onClick(e)
        }
      })
    } else if (typeof onClick === 'function') {
      onClick(e)
    }
  }

  /**
  * If an href is present, then render a Link, otherwise render a button.
  */
  if (href) {
    return (
      <Link
        role="button"
        disabled={disabled || isLoading}
        href={href}
        trackEvent={trackEvent}
        eventCategory={eventCategory}
        eventAction={eventAction}
        eventLabel={eventLabel}
        eventValue={eventValue}
        onClick={onClick}
        {...rest}
        className={classes.join(' ')}
      >
        {children}
      </Link>
    )
  }

  return (
    <button
      type="button"
      disabled={disabled || isLoading}
      onClick={onClickHandler}
      {...rest}
      className={classes.join(' ')}
    >
      {children}
    </button>
  )
}

Button.defaultProps = {
  isOutlined: false,
  isText: false,
  isInverted: false,
  isFullwidth: false,
  isActive: false,
  trackEvent: false,
}

Button.propTypes = {
  // Size of button. Options are: "small", "medium", "large".
  size: PropTypes.string,
  // Button has outlined styles.
  isOutlined: PropTypes.bool,
  // Button is text button.
  isText: PropTypes.bool,
  // Button colors are inverted. Use on dark background.
  isInverted: PropTypes.bool,
  // Button is fullwidth.
  isFullwidth: PropTypes.bool,
  // Button styles display the active state.
  isActive: PropTypes.bool,
  // If the event should be recorded with GA.
  trackEvent: PropTypes.bool,
  // Category for event tracking.
  eventCategory: PropTypes.string,
  // Action for event tracking.
  eventAction: PropTypes.string,
  // Label for event tracking.
  eventLabel: PropTypes.string,
  // Value for event tracking.
  eventValue: PropTypes.number,
}

export default Button
